import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import type { ActivePopup, ChartId, ChartInfo, TransientBroadcastDragOverride, RatioDropdown } from '../types/commontypes';
import type { ToolbarState } from '../contexts/GlobalShapesContext';
import { COLORS } from '../constants/colors';

export interface YCoordinateClickedData {
  value: number;
  chartId: ChartId;
}

// ─── Types ─────────────────────────────────────────────────────────

interface ChartUIStoreState {
  /**
   * Y축 가격 라벨의 (+) 버튼을 클릭하면 표시되는 팝업에서 "수평선 그리기"를 선택한 위치 정보
   */
  yCoordinateClicked: YCoordinateClickedData | undefined;
  /**
   * 차트 위에 오버레이되는 요소들의 데이터
   * key: chartId
   * value: { overrideData: OverrideStructure, isMeasureStart: boolean, isPopupVisible: boolean, ratioDropdown: RatioDropdown, chartClicked: boolean, locked: boolean, yCoordinateValue: number | undefined, activePopup: ActivePopup, yAccessorValue: number | undefined, miniToolbarState: ToolbarState, miniToolbarBoundsEl: HTMLElement | null, chartInfos: ChartInfo[] }
   */
  overrideData: TransientBroadcastDragOverride;
  isMeasureStart: boolean;
  isPopupVisible: boolean;
  ratioDropdown: RatioDropdown;
  chartClicked: boolean;
  locked: boolean;
  yCoordinateValue: number | undefined;
  activePopup: ActivePopup;
  yAccessorValue: number | undefined;
  miniToolbarState: ToolbarState;
  miniToolbarBoundsEl: HTMLElement | null;
  chartInfos: ChartInfo[];
}

interface ChartUIStoreActions {
  setYCoordinateClicked: (
    value:
      | YCoordinateClickedData
      | undefined
      | ((prev: YCoordinateClickedData | undefined) => YCoordinateClickedData | undefined),
  ) => void;
  setOverrideData: (data: TransientBroadcastDragOverride) => void;
  setIsMeasureStart: (value: boolean) => void;
  setIsPopupVisible: (value: boolean) => void;
  setRatioDropdown: (data: Partial<RatioDropdown>) => void;
  setChartClicked: (value: boolean) => void;
  setLocked: (value: boolean) => void;
  setYCoordinateValue: (value: number | undefined) => void;
  setActivePopup: (popup: ActivePopup) => void;
  setYAccessorValue: (value: number | undefined) => void;
  setMiniToolbarState: (value: ToolbarState | ((prev: ToolbarState) => ToolbarState)) => void;
  setMiniToolbarBoundsEl: (el: HTMLElement | null) => void;
  setChartInfos: (value: ChartInfo[] | ((prev: ChartInfo[]) => ChartInfo[])) => void;
}

export type ChartUIStore = ChartUIStoreState & ChartUIStoreActions;

// ─── localStorage helpers ─────────────────────────────────────────

const DEFAULT_TOOLBAR_SIZE = { width: 350, height: 48 };

function loadMiniToolbarState(timeframe: string): ToolbarState {
  try {
    const stored = localStorage.getItem(`miniToolbarState_${timeframe}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        x: parsed.x ?? (typeof window !== 'undefined' ? window.innerWidth / 2 : 0),
        y: parsed.y ?? (typeof window !== 'undefined' ? window.innerHeight / 2 : 0),
        width: parsed.width ?? DEFAULT_TOOLBAR_SIZE.width,
        height: parsed.height ?? DEFAULT_TOOLBAR_SIZE.height,
      };
    }
  } catch {
    // ignore
  }
  return {
    x: typeof window !== 'undefined' ? window.innerWidth / 2 : 0,
    y: typeof window !== 'undefined' ? window.innerHeight / 2 : 0,
    ...DEFAULT_TOOLBAR_SIZE,
  };
}

function loadChartInfos(timeframe: string, fallback?: ChartInfo[]): ChartInfo[] {
  try {
    const stored = localStorage.getItem(`chartInfos_${timeframe}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore
  }
  return fallback ?? [];
}

// ─── Store Factory ────────────────────────────────────────────────

export type ChartUIStoreInstance = ReturnType<typeof createChartUIStore>;

/**
 * timeframe마다 독립 store를 생성한다.
 * vanilla store (React 비의존)로 생성하여 class 컴포넌트에서도 getState()로 접근 가능.
 */
export function createChartUIStore(
  timeframe: string,
  initialChartInfos?: ChartInfo[],
  initialYCoordinateValue?: YCoordinateClickedData,
) {
  const store = createStore<ChartUIStore>((set, _get) => ({
    // ── State ──
    /**
     * Y축 가격 라벨의 (+) 버튼을 클릭하면 표시되는 팝업에서 "수평선 그리기"를 선택했을 위치 정보
     */
    yCoordinateClicked: initialYCoordinateValue,
    /**
     * 차트 위에 오버레이되는 요소들의 데이터
     * key: chartId
     * value: { overrideData: OverrideStructure, isMeasureStart: boolean, isPopupVisible: boolean, ratioDropdown: RatioDropdown, chartClicked: boolean, locked: boolean, yCoordinateValue: number | undefined, activePopup: ActivePopup, yAccessorValue: number | undefined, miniToolbarState: ToolbarState, miniToolbarBoundsEl: HTMLElement | null, chartInfos: ChartInfo[] }
     */
    overrideData: {},
    isMeasureStart: false,
    isPopupVisible: false,
    ratioDropdown: { uuid: '', hovering: false, position: { x: 0, y: 0 }, positionType: 'long', color: COLORS.long },
    chartClicked: false,
    locked: false,
    yCoordinateValue: undefined,
    activePopup: null,
    yAccessorValue: undefined,
    miniToolbarState: loadMiniToolbarState(timeframe),
    miniToolbarBoundsEl: null,
    chartInfos: loadChartInfos(timeframe, initialChartInfos),

    // ── Actions ──
    setYCoordinateClicked: valueOrUpdater =>
      set(s => ({
        yCoordinateClicked:
          typeof valueOrUpdater === 'function' ? valueOrUpdater(s.yCoordinateClicked) : valueOrUpdater,
      })),

    setOverrideData: data => set({ overrideData: data }),
    setIsMeasureStart: value => set({ isMeasureStart: value }),
    setIsPopupVisible: value => set({ isPopupVisible: value }),

    setRatioDropdown: data => set(s => ({ ratioDropdown: { ...s.ratioDropdown, ...data } })),

    setChartClicked: value => set({ chartClicked: value }),
    setLocked: value => set({ locked: value }),
    setYCoordinateValue: value => set({ yCoordinateValue: value }),
    setActivePopup: popup => set({ activePopup: popup }),
    setYAccessorValue: value => set({ yAccessorValue: value }),

    setMiniToolbarState: valueOrUpdater =>
      set(s => ({
        miniToolbarState: typeof valueOrUpdater === 'function' ? valueOrUpdater(s.miniToolbarState) : valueOrUpdater,
      })),

    setMiniToolbarBoundsEl: el => set({ miniToolbarBoundsEl: el }),

    setChartInfos: valueOrUpdater =>
      set(s => ({
        chartInfos: typeof valueOrUpdater === 'function' ? valueOrUpdater(s.chartInfos) : valueOrUpdater,
      })),
  }));

  // ── localStorage subscribe (debounced) ──
  let miniToolbarTimer: ReturnType<typeof setTimeout> | undefined;
  let chartInfosTimer: ReturnType<typeof setTimeout> | undefined;

  const unsubscribe = store.subscribe((state, prevState) => {
    // miniToolbarState persist
    if (state.miniToolbarState !== prevState.miniToolbarState) {
      clearTimeout(miniToolbarTimer);
      miniToolbarTimer = setTimeout(() => {
        try {
          localStorage.setItem(`miniToolbarState_${timeframe}`, JSON.stringify(state.miniToolbarState));
        } catch {
          /* ignore */
        }
      }, 500);
    }

    // chartInfos persist
    if (state.chartInfos !== prevState.chartInfos) {
      clearTimeout(chartInfosTimer);
      chartInfosTimer = setTimeout(() => {
        try {
          localStorage.setItem(`chartInfos_${timeframe}`, JSON.stringify(state.chartInfos));
        } catch {
          /* ignore */
        }
      }, 500);
    }
  });

  // ── Transient Override Channel ──────────────────────────────────
  // zustand 바깥의 경량 pub/sub. 드래그 중 overrideData를 zustand set()으로 쓰면
  // 전체 구독자 비교 오버헤드가 발생한다. 이 채널은 zustand을 거치지 않으므로
  // 구독한 클래스 컴포넌트(VertLine 등)만 forceUpdate로 재렌더한다.
  // 향후 OrderLine 등 다른 도형의 실시간 데이터 갱신에도 동일 패턴을 적용할 수 있다.
  let _transientOverride: TransientBroadcastDragOverride = {};
  const _overrideListeners = new Set<(data: TransientBroadcastDragOverride) => void>();

  // destroy helper — unsubscribe + clear timers
  return Object.assign(store, {
    destroy: () => {
      unsubscribe();
      clearTimeout(miniToolbarTimer);
      clearTimeout(chartInfosTimer);
      _overrideListeners.clear();
    },
    /** zustand을 거치지 않고 override 데이터를 설정하고 구독자에게 통지한다. */
    setTransientOverride: (data: TransientBroadcastDragOverride) => {
      _transientOverride = data;
      _overrideListeners.forEach(l => l(data));
    },
    /** 현재 transient override 값을 반환한다. */
    getTransientOverride: () => _transientOverride,
    /** transient override 변경을 구독한다. unsubscribe 함수를 반환한다. */
    subscribeOverride: (listener: (data: TransientBroadcastDragOverride) => void) => {
      _overrideListeners.add(listener);
      return () => {
        _overrideListeners.delete(listener);
      };
    },
  });
}

// ─── Hooks ────────────────────────────────────────────────────────

/**
 * 전체 store 구독 — store 인스턴스를 인자로 받는다 (factory 패턴).
 */
export function useChartUIStore(store: ChartUIStoreInstance) {
  return useStore(store, s => s);
}

/**
 * selector 기반 구독 — 필요한 필드만 구독하여 불필요한 리렌더링 방지.
 *
 * @example
 * const isPopupVisible = useChartUIStoreSelector(store, s => s.isPopupVisible);
 */
export function useChartUIStoreSelector<T>(store: ChartUIStoreInstance, selector: (state: ChartUIStore) => T): T {
  return useStore(store, selector);
}
